import { DurationInputArg2 } from 'moment';

// all flags here, alphabetically sorted

/** Use this prompt to add comments to new flags after implementation:
 *
 * Add a comment description for this flag, explaining how it's used in the
 * codebase. Be brief, don't link to call sites, just describe features in plain
 * English.
 */

/** Max session length in ms before forced logout. Not wired up yet. */
export const ABSOLUTE_TIMEOUT = 'absolute-timeout';

/** Prompts users to refresh when a newer deployed version is available. */
export const APP_VERSION = 'app-version';

/** Enables the AI Ask panel, chat command, and Ask actions on cards and collection views. */
export const ASK = 'ask';

/** Capture card create flow. */
export const ASK_CARD_CREATE = 'ask-card-create';

/** Lets Ask turns carry file attachments and voice input. */
export const ASK_FILE_AND_VOICE = 'ask-file-and-voice';

/** Allows back-and-forth conversation in the Ask panel instead of single-turn only. */
export const ASK_MULTI_TURN = 'ask-multi-turn';

/** Shows inline Summarize buttons on cards; requires Ask to also be enabled. */
export const ASK_SUMMARIES = 'ask-summaries';

/** Shows automations UI inside card forms. Not wired up yet. */
export const AUTOMATIONS_IN_CARD_FORM = 'automations-in-card-form';

/** Shows bulk CSV upload on the invite-contacts screen. */
export const BATCH_UPLOAD_USERS = 'batch-upload-users';

/** Allows renaming internal (non-public) view templates in the builder. */
export const BUILDER_RENAME_INTERNAL_FORMS = 'builder-rename-internal-forms';

/** Allows permanently deleting workflow components in the builder. Not wired up yet. */
export const COMPONENT_DELETE = 'component-delete';

/** Shows CSV bulk import in channel settings. */
export const CSV_BULK_IMPORT = 'csv-bulk-import';

/** Refetches Apollo data after sleep/wake or when the browser comes back online. */
export const DATA_FRESHNESS = 'data-freshness';

/** Shows delete in the workspace section overflow menu. */
export const DELETE_WORKSPACE_SECTION = 'delete-workspace-section';

/** Unlocks the developer menu and internal builder field options (seven-tap to enable locally). */
export const DEVELOPER_TOOLS = 'developer-tools';

/** Shows duplicate in the workspace section overflow menu. */
export const DUPLICATE_WORKSPACE_SECTION = 'duplicate-workspace-section';

/** Lets users change a saved view's type (table, calendar, etc.) from view options. */
export const EDITABLE_VIEW_TYPE = 'editable-view-type';

/** Enables customizable hotkeys. */
export const HOT_KEYS = 'hot-keys';

/** Enables customizing hotkey bindings. While off, users get a fixed basic set. */
export const HOT_KEYS_CUSTOMIZATION = 'hot-keys-customization';

/** Logs users out after this many ms of inactivity; 0 disables. */
export const INACTIVITY_TIMEOUT = 'inactivity-timeout';

/** Adds a Mentions tab to the activity feed showing @mention notifications. */
export const MENTIONS = 'mentions';

/** Uses multi-group grouping controls instead of a single group in collection views. */
export const MULTIPLE_FILTER_GROUPS = 'multi-filter-groups';

/** Base interval in ms for debounced permission refetch after subscription events. */
export const PERMISSIONS_REFETCH_TIMER = 'permissions-refetch-timer';

/** Shows the required-field toggle in view template component settings. */
export const REQUIRED_FIELDS_IN_VIEW_TEMPLATE_BUILDER = 'required-fields-in-view-template-builder';

/** Shows User Groups in the main navigation menu. */
export const USER_GROUPS = 'user-groups';

/** Redesigned view template builder: layout-first with component settings in a right sidebar. */
export const VIEW_TEMPLATE_FIELD_SIDEBAR = 'view-template-field-sidebar';

export interface AppVersionFlag {
  version: string;
  frequency: DurationInputArg2; // e.g. 'day', 'week', etc.
}

const appVersionFlagDefault: AppVersionFlag = {
  version: import.meta.env.VITE_VERSION,
  frequency: 'week',
};

// set default flag state here, alphabetically sorted
export const defaultFlags = {
  [ABSOLUTE_TIMEOUT]: 0,
  [APP_VERSION]: appVersionFlagDefault,
  [ASK]: false,
  [ASK_CARD_CREATE]: false,
  [ASK_FILE_AND_VOICE]: false,
  [ASK_MULTI_TURN]: false,
  [ASK_SUMMARIES]: false,
  [AUTOMATIONS_IN_CARD_FORM]: false,
  [BATCH_UPLOAD_USERS]: false,
  [BUILDER_RENAME_INTERNAL_FORMS]: false,
  [COMPONENT_DELETE]: false,
  [CSV_BULK_IMPORT]: false,
  [DATA_FRESHNESS]: {
    enabled: false,
    logPrefix: '',
    maxRetries: 1,
    refetchThrottleMs: 1000,
    sleepDetectorIntervalMs: 1000,
    sleepDetectorTriggerRefetchMs: 1000,
  },
  [DELETE_WORKSPACE_SECTION]: false,
  [DEVELOPER_TOOLS]: false,
  [DUPLICATE_WORKSPACE_SECTION]: false,
  [EDITABLE_VIEW_TYPE]: false,
  [HOT_KEYS]: false,
  [HOT_KEYS_CUSTOMIZATION]: false,
  [INACTIVITY_TIMEOUT]: 0,
  [MENTIONS]: false,
  [MULTIPLE_FILTER_GROUPS]: false,
  [PERMISSIONS_REFETCH_TIMER]: 0,
  [REQUIRED_FIELDS_IN_VIEW_TEMPLATE_BUILDER]: false,
  [USER_GROUPS]: false,
  [VIEW_TEMPLATE_FIELD_SIDEBAR]: false,
};

export type Flags = typeof defaultFlags;
export type FlagName = keyof Flags;
